import { useState } from "react";

import { useDataSnapshot } from "../../api/queries";
import { Card, Kpi, Lede, PageHead } from "../../components/Card";
import { QueryState } from "../../components/States";
import { Table } from "../../components/Table";
import { clock, num, short } from "../../lib/format";
import { StoreActions } from "./StoreActions";
import { StoreSections } from "./StoreSections";

type Tab = "bars" | "signals";

export function DataSnapshotScreen(): React.ReactNode {
  const snapshot = useDataSnapshot();
  const [tab, setTab] = useState<Tab>("bars");
  const [selected, setSelected] = useState<number | null>(null);

  return (
    <QueryState status={snapshot.status} error={snapshot.error} what="数据快照" refetch={() => void snapshot.refetch()}>
      {snapshot.data ? (
        <>
          <PageHead title="原始输入" sub={`GET /data-snapshot · ${snapshot.data.snapshot_id ?? "无行情快照"}`} />
          <Lede>
            这里展示本次执行链路直接读取的原始输入。行情与信号按文件原样加载，点击表格行可查看完整 JSON。
          </Lede>
          <div className="grid g4">
            <Kpi label="行情记录" value={num(snapshot.data.bars.length, 0)} detail="bars.jsonl" />
            <Kpi label="信号记录" value={num(snapshot.data.signals.length, 0)} detail="signals.jsonl" />
            <Kpi label="快照指纹" value={<span className="mono">{short(snapshot.data.snapshot_id ?? "—", 16)}</span>} detail="行情内容摘要" />
            <Kpi label="标的" value={new Set(snapshot.data.bars.map((bar) => bar.instrument_id)).size} detail="行情中的 instrument_id" />
          </div>
          <Card title="输入来源" sub="服务端实际读取路径">
            <div className="kv">
              <div><dt>行情</dt><dd className="mono">{snapshot.data.bars_path}</dd></div>
              <div><dt>信号</dt><dd className="mono">{snapshot.data.signals_path}</dd></div>
            </div>
          </Card>
          <div className="tabs" role="tablist" aria-label="原始输入类型">
            <button type="button" className={tab === "bars" ? "on" : ""} onClick={() => { setTab("bars"); setSelected(null); }} role="tab" aria-selected={tab === "bars"}>行情 Bars</button>
            <button type="button" className={tab === "signals" ? "on" : ""} onClick={() => { setTab("signals"); setSelected(null); }} role="tab" aria-selected={tab === "signals"}>信号 Signals</button>
          </div>
          {tab === "bars" ? (
            <Card title="原始行情" sub={`${snapshot.data.bars.length} 条`} nopad>
              <Table
                cols={[{ label: "时间" }, { label: "标的" }, { label: "开", num: true }, { label: "高", num: true }, { label: "低", num: true }, { label: "收", num: true }, { label: "成交量", num: true }]}
                rows={snapshot.data.bars.map((bar, index) => ({
                  key: `${bar.ts_event}-${bar.instrument_id}`,
                  selected: selected === index,
                  onClick: () => setSelected(selected === index ? null : index),
                  cells: [clock(bar.ts_event), bar.instrument_id, num(bar.open, 2), num(bar.high, 2), num(bar.low, 2), num(bar.close, 2), num(bar.volume, 0)],
                }))}
                empty={`没有行情快照（${snapshot.data.bars_path} 不存在）`}
                tall
              />
              {selected !== null ? <RawJson value={snapshot.data.bars[selected]} /> : null}
            </Card>
          ) : (
            <Card title="原始信号" sub={`${snapshot.data.signals.length} 条`} nopad>
              <Table
                cols={[{ label: "signal_id", mono: true }, { label: "标的" }, { label: "方向" }, { label: "目标仓位", num: true }, { label: "有效期" }, { label: "来源", wrap: true }]}
                rows={snapshot.data.signals.map((signal, index) => ({
                  key: `${String(signal.signal_id ?? index)}-${index}`,
                  selected: selected === index,
                  onClick: () => setSelected(selected === index ? null : index),
                  cells: [<span className="mono">{String(signal.signal_id ?? "—")}</span>, String(signal.instrument_id ?? "—"), String(signal.direction ?? "—"), num(Number(signal.target_weight ?? 0), 2), `${clock(Number(signal.valid_from ?? 0))} ~ ${clock(Number(signal.valid_until ?? 0))}`, <span className="mono">{JSON.stringify(signal.source ?? {})}</span>],
                }))}
                empty={`没有信号输入（${snapshot.data.signals_path} 不存在或为空）`}
                tall
              />
              {selected !== null ? <RawJson value={snapshot.data.signals[selected]} /> : null}
            </Card>
          )}
          <StoreSections />
          <StoreActions />
        </>
      ) : null}
    </QueryState>
  );
}

function RawJson({ value }: { value: unknown }): React.ReactNode {
  return <pre className="code" style={{ marginTop: 0 }}>{JSON.stringify(value, null, 2)}</pre>;
}
